import React, { useCallback, useContext } from "react";
import { getHeaderTitle } from "@react-navigation/elements";
import { useI18n } from "../../hooks/i18n-hooks";
import ScreenHeader from "../../screens/user/ScreenHeader";
import SalariesScreen from "../../screens/worker/Salaries";
import TabBar from "../../components/TabBar";
import TimeSheetListScreen from "../../screens/user/TimeSheetListScreen";
import UserActivitiesNavigation from "../user/ActivitiesNavigation";
import Icon from "../../components/Icon";
import UserMissionsNavigation, { UserMissionsTabParamList } from "../user/UserMissionsNavigation";
import { useNavigationNavigator } from "../useNavigationNavigator";
import UserAvatar from "../../screens/user/UserAvatar";
import IntercomScreen from "../../screens/user/Intercom";
import { AuthManagerContext } from "../../contexts/AuthManager";
import { ProfileSwitcherManagerContext } from "../../contexts/ProfileSwitcherManager";
import { EventArg, NavigatorScreenParams } from "@react-navigation/native";
import { useIntercom } from "../../hooks/intercom";
import ProfileStack from "../ProfileStack";
import { useTracking } from "../../hooks/tracking";

export type WorkerTabParamList = {
    MissionsScreen: NavigatorScreenParams<UserMissionsTabParamList>;
    TimeSheetScreen: undefined;
    SalariesScreen: undefined;
    ActivitiesTab: undefined;
    Intercom: undefined;
    ProfileStack: undefined;
};

export default function HomeScreen() {
    const i18n = useI18n();
    const { openProfileSwitch, profileId, unreadProfilesActivities } = useContext(
        ProfileSwitcherManagerContext,
    );
    const { user } = useContext(AuthManagerContext);
    const { NavigationNavigator, navigatorProps } = useNavigationNavigator();
    const { trackEvent } = useTracking();
    const { presentIntercom } = useIntercom();

    const intercomScreenListener = useCallback(
        () => ({
            tabPress: (e: EventArg<"tabPress", true>) => {
                e.preventDefault();
                void trackEvent("click_show_help");
                void presentIntercom();
            },
        }),
        [presentIntercom, trackEvent],
    );

    const listeners = useCallback(
        () => ({
            tabLongPress: openProfileSwitch,
        }),
        [openProfileSwitch],
    );

    return (
        <NavigationNavigator.Navigator
            {...navigatorProps}
            screenOptions={{
                ...navigatorProps.screenOptions,
                freezeOnBlur: false,
                lazy: false,
            }}
            initialRouteName="MissionsScreen"
            tabBar={(props) => <TabBar {...props} />}
        >
            <NavigationNavigator.Screen
                name="MissionsScreen"
                component={UserMissionsNavigation}
                options={{
                    tabBarIcon: (props) => <Icon {...props} name="mission" />,
                    headerShown: false,
                    title: i18n.t("screens.user.tabs.missions"),
                }}
            />
            <NavigationNavigator.Screen
                name="TimeSheetScreen"
                component={TimeSheetListScreen}
                options={{
                    tabBarIcon: (props) => <Icon {...props} name="timesheet" />,
                    title: i18n.t("screens.user.tabs.time_sheets"),
                    headerTitle: i18n.t("timesheet_list.title"),
                    header: ({ route, options }) => {
                        const title = getHeaderTitle(options, route.name);
                        return <ScreenHeader label={title}></ScreenHeader>;
                    },
                }}
            />
            <NavigationNavigator.Screen
                name="SalariesScreen"
                component={SalariesScreen}
                options={{
                    tabBarIcon: (props) => <Icon {...props} name="invoice" />,
                    title: i18n.t("screens.user.tabs.salaries"),
                    header: ({ route, options }) => {
                        const title = getHeaderTitle(options, route.name);
                        return <ScreenHeader label={title}></ScreenHeader>;
                    },
                }}
            />
            <NavigationNavigator.Screen
                name="Intercom"
                component={IntercomScreen} // keep this screen if a navigate() goes there
                options={{
                    tabBarIcon: (props) => <Icon {...props} name="circle-question" />,
                    title: i18n.t("screens.client_profile.tabs.help.title"),
                }}
                listeners={intercomScreenListener}
            />
            <NavigationNavigator.Screen
                name="ActivitiesTab"
                component={UserActivitiesNavigation}
                options={{
                    tabBarIcon: (props) => <Icon {...props} name="todo" />,
                    title: i18n.t("screens.user.tabs.todos"),
                    tabBarBadge: profileId ? unreadProfilesActivities[profileId] : 0,
                    headerShown: false,
                }}
            />
            <NavigationNavigator.Screen
                name="ProfileStack"
                component={ProfileStack}
                options={{
                    title: i18n.t("screens.client_profile.tabs.profile.title"),
                    headerShown: false,
                    tabBarIcon: () => (
                        <UserAvatar size={30} avatarPath={user?.avatarUrl} canEnlarge={false} />
                    ),
                }}
                listeners={listeners}
            />
        </NavigationNavigator.Navigator>
    );
}
